import ButtonLink from 'components/ButtonLink'
import Radio, { CombinedRadios, RadioLabel } from 'components/Radio'
import copy from 'copy.json'
import { SubtractIcon } from 'icons'
import type { HTMLAttributes } from 'react'
import { useState } from 'react'
import styled from 'styled-components'
import type { PricingType, YearlyData, Zone } from 'types/match'
import { toCurrency } from 'utils'
import Badge from './Badge'
import RdnPricingDetailsAction from './RdnPricingDetailsAction'
import type { RdnCalculationDetails } from 'types/match'
import type { ImbalanceDetails } from 'types/match'
import ImbalanceVisualization from './ImbalanceVisualization'

const texts = copy.page.matches.year_summary

type Props = {
  data: YearlyData | null
  onShowZones: (zones: Zone[]) => void
  downloadIndividualEnergyPricingFile?: () => void
  loadRdnPricingDetails?: () => Promise<RdnCalculationDetails | null>
  loadImbalanceDetails?: () => Promise<ImbalanceDetails | null>
} & HTMLAttributes<HTMLDivElement>

type EnergySavingProps = {
  data: YearlyData
  pricingType: PricingType
  totalCost: number
}

function EnergySaving({ data, pricingType, totalCost }: EnergySavingProps) {
  return (
    <Saving>
      {texts.annual_saving}
      <div>
        <SubtractIcon />
        {toCurrency(data.cost_details[pricingType].current_total_required_energy_price - totalCost)}
      </div>
    </Saving>
  )
}

export default function YearSummary({
  data,
  onShowZones,
  downloadIndividualEnergyPricingFile = () => {},
  loadRdnPricingDetails,
  loadImbalanceDetails,
  ...props
}: Props) {
  const [pricingType, setPricingType] = useState<PricingType>('GENERAL')
  const [detailsExpanded, setDetailsExpanded] = useState(false)

  if (!data) return null

  const isIndividualPricingAvailable = !!data.cost_details.INDIVIDUAL

  const totalPpaPrice = data.cost_details[pricingType].total_green_energy_price
  const totalTrancheCost = data.cost_details[pricingType].total_tranche_energy_price
  const totalShortageCost = data.cost_details[pricingType].total_shortage_price
  const totalSurplusSellPrice = data.cost_details[pricingType].total_surplus_sell_price
  const additionalCoverageCosts = data.cost_details[pricingType].additional_coverage_costs
  const totalCost =
    totalPpaPrice +
    totalTrancheCost +
    totalShortageCost +
    additionalCoverageCosts -
    totalSurplusSellPrice
  const averageCost = totalCost / (data.total_coverage + data.total_shortage)

  return (
    <div {...props}>
      <Heading>{texts.heading}</Heading>

      <ButtonLink onClick={() => setDetailsExpanded(!detailsExpanded)}>
        {detailsExpanded ? texts.hide_details : texts.show_details}
      </ButtonLink>
      {loadRdnPricingDetails && <RdnPricingDetailsAction loadDetails={loadRdnPricingDetails} />}
      {loadImbalanceDetails && <ImbalanceVisualization loadDetails={loadImbalanceDetails} />}

      <Wrapper>
        <Average>
          <h4>{texts.coverage_percentage}</h4>
          <div>
            <Badge>{Math.round(data.coverage_percentage)}%</Badge>
            <button
              className="green text"
              onClick={() => onShowZones(Object.values(data.zones_data))}
            >
              {texts.show_zones}
            </button>
          </div>
        </Average>
        <Row>
          <Column>
            <span>{texts.total_coverage}:</span>
            <Badge coverage>
              {data.total_coverage} {copy.common.MWh}
            </Badge>
            <Badge>{Math.round(data.coverage_percentage)}%</Badge>
          </Column>

          <Column>
            <span>{texts.total_shortage}:</span>
            <Badge shortage>
              {data.total_shortage} {copy.common.MWh}
            </Badge>
            <Badge>{Math.round(data.shortage_percentage)}%</Badge>
          </Column>

          <Column>
            <span>{texts.total_surplus}:</span>
            <Badge surplus>
              {data.total_surplus} {copy.common.MWh}
            </Badge>
            <Badge>{Math.round(data.surplus_percentage)}%</Badge>
          </Column>
        </Row>
        <hr />

        <h4>{texts.energy_purchase_cost}</h4>

        {isIndividualPricingAvailable && (
          <PricingSelector>
            <p>{texts.energy_pricing}</p>
            <CombinedRadios>
              <RadioLabel>
                <Radio
                  name="pricing"
                  value="GENERAL"
                  checked={pricingType === 'GENERAL'}
                  onChange={() => setPricingType('GENERAL')}
                />
                {texts.general}
              </RadioLabel>
              <RadioLabel>
                <Radio
                  name="pricing"
                  value="INDIVIDUAL"
                  checked={pricingType === 'INDIVIDUAL'}
                  onChange={() => setPricingType('INDIVIDUAL')}
                />
                {texts.individual}
              </RadioLabel>
            </CombinedRadios>
          </PricingSelector>
        )}

        <Row>
          <Column>
            <span>{texts.current_supplier}</span>
            <strong>
              {toCurrency(data.cost_details[pricingType].current_total_required_energy_price)}
            </strong>
            <span>{texts.average_1mwh_price}</span>
            <strong>{toCurrency(data.cost_details[pricingType].current_energy_price)}</strong>
          </Column>

          {!detailsExpanded && (
            <Column>
              <span>Oferta</span>
              <strong title={copy.page.matches.total_cost_description}>
                {toCurrency(totalCost)}
              </strong>
              <span>{texts.average_1mwh_price}</span>
              <strong title={copy.page.matches.total_cost_description}>
                {toCurrency(averageCost)}
              </strong>
            </Column>
          )}

          {detailsExpanded && (
            <>
              <Column>
                <span>{texts.OZE_energy}</span>
                <strong title={copy.page.matches.no_excise_guarantee}>
                  {toCurrency(totalPpaPrice)}
                </strong>
                <span>{texts.average_1mwh_price}</span>
                <strong title={copy.page.matches.no_excise_guarantee}>
                  {toCurrency(data.cost_details[pricingType].green_energy_price)}
                </strong>
                <span>Energia TGE</span>
                <strong title={copy.page.matches.no_excise_guarantee}>
                  {toCurrency(totalTrancheCost)}
                </strong>
                <span>{texts.average_1mwh_price}</span>
                <strong title={copy.page.matches.no_excise_guarantee}>
                  {toCurrency(data.cost_details[pricingType].tranche_energy_price)}
                </strong>
              </Column>

              <Column>
                <span>{texts.total_shortage_price}</span>
                <strong title={copy.page.matches.no_excise_guarantee}>
                  {toCurrency(totalShortageCost)}
                </strong>
                <span>{texts.average_1mwh_price}</span>
                <strong title={copy.page.matches.no_excise_guarantee}>
                  {toCurrency(data.cost_details[pricingType].shortage_price)}
                </strong>
                <span>{texts.total_surplus_sell_price}</span>
                <strong>{toCurrency(totalSurplusSellPrice)}</strong>
                <span>{texts.average_1mwh_price}</span>
                <strong>{toCurrency(data.cost_details[pricingType].surplus_sell_price)}</strong>
              </Column>
            </>
          )}

          {!detailsExpanded && (
            <Column>
              <EnergySaving data={data} pricingType={pricingType} totalCost={totalCost} />
            </Column>
          )}
        </Row>

        {detailsExpanded && (
          <Row>
            <Column>
              <EnergySaving data={data} pricingType={pricingType} totalCost={totalCost} />
            </Column>
          </Row>
        )}

        {isIndividualPricingAvailable && (
          <DownloadButton onClick={downloadIndividualEnergyPricingFile}>
            {texts.download_individual_energy_pricing}
          </DownloadButton>
        )}
      </Wrapper>
    </div>
  )
}

const Wrapper = styled.div`
  max-width: 100%;
  overflow-x: auto;
  background: var(--very-light-green);
  border: 1px solid var(--green);
  border-radius: 4px;
  gap: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  margin-top: 16px;

  h4 {
    font: var(--600-13);
  }

  hr {
    width: 100%;
    margin: 0;
    border: none;
    border-bottom: 1px solid var(--green);
  }

  strong {
    font: var(--600-15);
    margin-bottom: 3px;
  }
`

const Average = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  > div {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    ${Badge} {
      font: var(--600-24);
    }
  }
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  position: relative;

  span:not(${Badge}) {
    color: var(--gray);
    font: var(--600-12);
  }
`

const Saving = styled.div`
  font: var(--600-13);
  color: var(--text);

  div {
    display: flex;
    gap: 8px;
    margin-top: 5px;

    font: var(--800-24);
    color: var(--green);
  }
`

const PricingSelector = styled.div`
  ${CombinedRadios} {
    flex-direction: row;
    gap: 0;
  }

  p {
    margin-bottom: 5px;
    font: var(--600-13);
    color: var(--gray);
  }

  label {
    padding: 10px;
    width: 100%;
    max-width: 160px;

    font: var(--400-13);
    border: 1px solid var(--green);
    border-radius: 4px;
  }

  label:first-of-type {
    border-radius: 4px 0 0 4px;
  }

  label:last-of-type {
    border-radius: 0 4px 4px 0;
  }
`

const DownloadButton = styled.button`
  background: none;
  border: none;

  color: var(--green);
  font: var(--400-13);

  cursor: pointer;
  align-self: start;
  padding: 0;
`

const Heading = styled.h3`
  display: inline;
  margin-right: 10px;
`
